import { useTranslation } from "react-i18next";
import { Panel } from 'primereact/panel';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useState } from "react";
import segmento from "../../../assets/score_pj_boleto.jpeg";
import { ScoreBar } from "../../../components/ScoreBar";

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function getRiscoConfig(mensagemRestritiva: string) {
  const nivel = (mensagemRestritiva || '').toUpperCase();
  if (nivel === 'ALTÍSSIMO' || nivel === 'ALTISSIMO') {
    return { color: '#DE1616', label: 'Altíssimo', bg: '#FDE8E8', text: '#9B1C1C', border: '#F8B4B4', range: '300 a 400' };
  }
  if (nivel === 'MUITO ALTO') {
    return { color: '#DE1616', label: 'Muito Alto', bg: '#FDE8E8', text: '#9B1C1C', border: '#F8B4B4', range: '400 a 500' };
  }
  if (nivel === 'ALTO') {
    return { color: '#FF9700', label: 'Alto', bg: '#FFF3E0', text: '#B45309', border: '#FCD34D', range: '500 a 600' };
  }
  if (nivel === 'MEDIO' || nivel === 'MÉDIO') {
    return { color: '#FFCA39', label: 'Médio', bg: '#FEFCE8', text: '#854D0E', border: '#FEF08A', range: '600 a 700' };
  }
  if (nivel === 'BAIXO') {
    return { color: '#0DAB01', label: 'Baixo', bg: '#DEF7EC', text: '#03543F', border: '#84E1BC', range: '700 a 900' };
  }
  if (nivel === 'MÍNIMO' || nivel === 'MUITO BAIXO') {
    return { color: '#1FA685', label: 'Muito Baixo', bg: '#ECFDF5', text: '#065F46', border: '#6EE7B7', range: '900 a 1000' };
  }
  return { color: '#9CA3AF', label: mensagemRestritiva, bg: '#F3F4F6', text: '#374151', border: '#D1D5DB', range: '' };
}

// ─────────────────────────────────────────────────────────────────────────────
// SCORE GAUGE – versão Highcharts (preserva comportamento original)
// ─────────────────────────────────────────────────────────────────────────────
function ScoreGaugeHighcharts({ score, labelColor }: { score: any; labelColor: string }) {
  const options = {
    chart: {},
    title: {
      text: score.score + '',
      align: 'center',
      verticalAlign: 'middle',
      y: 90,
      style: { fontSize: '40px', color: labelColor },
    },
    credits: { enabled: false },
    tooltip: { enabled: false },
    plotOptions: {
      pie: {
        allowPointSelect: false,
        dataLabels: { enabled: false, distance: -50, style: { fontWeight: 'bold', color: 'white' } },
        startAngle: -90,
        endAngle: 90,
        center: ['50%', '75%'],
      },
    },
    series: [{
      type: 'pie',
      name: '',
      innerSize: '60%',
      data: [
        { name: '', y: score.score, color: labelColor, selected: true, showInLegend: false },
        { name: '', y: 1000 - score.score, color: '#ccc', selected: true, showInLegend: false },
      ],
    }],
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// SCORE GAUGE – versão SVG nativa
// ─────────────────────────────────────────────────────────────────────────────
function ScoreGaugeSVG({ score, color }: { score: number; color: string }) {
  const radius = 70;
  const strokeWidth = 14;
  const circumference = Math.PI * radius; // semicircle
  const percentage = Math.max(0, Math.min(1000, score)) / 1000;
  const strokeDashoffset = circumference - percentage * circumference;

  // Pointer angle on the arc: -180deg (left) → 0deg (right)
  const angle = -180 + percentage * 180;
  const rad = (angle * Math.PI) / 180;
  const cx = 100;
  const cy = 96;
  const px = cx + radius * Math.cos(rad);
  const py = cy + radius * Math.sin(rad);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <svg width="200" height="110" viewBox="0 0 200 110" style={{ overflow: 'visible' }}>
        {/* Track */}
        <path
          d="M 30,96 A 70,70 0 0,1 170,96"
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Value arc */}
        <path
          d="M 30,96 A 70,70 0 0,1 170,96"
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.8s ease-in-out' }}
        />
        {/* Pointer dot */}
        <circle cx={px} cy={py} r={7} fill={color} stroke="#FFF" strokeWidth={2} />
        {/* Score text */}
        <text x="100" y="88" textAnchor="middle" fontSize="28" fontWeight="800" fill={color}>
          {score}
        </text>
        <text x="100" y="104" textAnchor="middle" fontSize="10" fill="#9CA3AF" fontWeight="600">
          de 1000
        </text>
      </svg>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RISK BAR – faixa de classificação horizontal
// ─────────────────────────────────────────────────────────────────────────────
function RiskBar({ scoreValue }: { scoreValue: number }) {
  const position = Math.min(100, Math.max(0, ((scoreValue - 300) / 700) * 100));

  const ranges = [
    { label: 'Muito Alto', range: '300 a 500', color: '#DE1616', width: '28.5%' },
    { label: 'Alto', range: '500 a 600', color: '#FF9700', width: '14.3%' },
    { label: 'Médio', range: '600 a 700', color: '#FFCA39', width: '14.3%' },
    { label: 'Baixo', range: '700 a 900', color: '#0DAB01', width: '28.5%' },
    { label: 'Muito Baixo', range: '900 a 1000', color: '#1FA685', width: '14.3%' },
  ];

  return (
    <div>
      {/* Gradient bar */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '14px',
          borderRadius: '7px',
          background:
            'linear-gradient(to right, #DE1616 0%, #DE1616 28.5%, #FF9700 28.5%, #FF9700 42.8%, #FFCA39 42.8%, #FFCA39 57.1%, #0DAB01 57.1%, #0DAB01 85.6%, #1FA685 85.6%, #1FA685 100%)',
          marginBottom: '10px',
          marginTop: '8px',
        }}
      >
        {/* Marker */}
        <div
          style={{
            position: 'absolute',
            left: `${position}%`,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '6px',
            height: '22px',
            borderRadius: '3px',
            backgroundColor: '#1F2937',
            border: '1px solid #111827',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            transition: 'left 0.8s ease-in-out',
          }}
        />
      </div>

      {/* Labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
        {ranges.map(r => (
          <div key={r.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: r.color,
                display: 'inline-block',
                marginBottom: '2px',
              }}
            />
            <span style={{ fontSize: '0.65rem', color: '#6B7280', fontWeight: 600, textAlign: 'center' }}>
              {r.label}
            </span>
            <span style={{ fontSize: '0.6rem', color: '#9CA3AF', textAlign: 'center' }}>{r.range}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function Score({
  label,
  score,
  scoreBoleto,
  isConsultaNova,
}: {
  label: string;
  score: any;
  scoreBoleto: boolean;
  isConsultaNova?: boolean;
}) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  // Toggle between Highcharts and SVG in the new layout
  const [useHighcharts, setUseHighcharts] = useState(false);

  // Legacy color resolution
  const riscoAltissimo = '#DE1616';
  const riscoMuitoAlto = '#DE1616';
  const riscoAlto = '#FF9700';
  const riscoMedio = '#ffca39';
  const riscoBaixo = '#0DAB01';
  const riscoMinimo = '#1FA685';

  let labelColor = riscoAlto;
  if (score.mensagemRestritiva.toUpperCase() === 'ALTÍSSIMO') {
    labelColor = riscoAltissimo;
  } else if (score.mensagemRestritiva.toUpperCase() === 'MUITO ALTO') {
    labelColor = riscoMuitoAlto;
  } else if (score.mensagemRestritiva.toUpperCase() === 'ALTO') {
    labelColor = riscoAlto;
  } else if (score.mensagemRestritiva.toUpperCase() === 'MEDIO' || score.mensagemRestritiva.toUpperCase() === "MÉDIO") {
    labelColor = riscoMedio;
  } else if (score.mensagemRestritiva.toUpperCase() === 'BAIXO') {
    labelColor = riscoBaixo;
  } else if (score.mensagemRestritiva.toUpperCase() === 'MÍNIMO' || score.mensagemRestritiva.toUpperCase() === 'MUITO BAIXO') {
    labelColor = riscoMinimo;
  }

  const riscoConfig = getRiscoConfig(score.mensagemRestritiva);

  const footerContent = (
    <div>
      <Button label="Fechar" icon="pi pi-times" style={{ color: '#183462' }} onClick={() => setVisible(false)} className="p-button-text" />
    </div>
  );

  const EntendaScoreContent = () => (
    <>
      {scoreBoleto ? (
        <>
          <p>
            O Quod Score PJ com boletos tem como objetivo avaliar o risco de crédito das Empresas por meio de pontuações que variam <b>entre 300 e 1000</b>, sendo quanto mais alta, menor a chance de inadimplência nos próximos 6 meses e inversamente proporcional.
          </p>
          <h4 className="m-0 font-weight-bold text-success">Principais características da solução</h4>
          <p>Solução foi desenvolvida com o objetivo de atingir a melhor performance para cada tipo e perfil de cliente, na sua construção os seguintes dados podem ser utilizados com intuito de atingir esta melhor performance e assertividade <b>*</b></p>
          <ul className="list-group">
            <li className="list-group-item">Mais de 250 atributos do cadastro positivo.</li>
            <li className="list-group-item">Mais de 230 atributos de boletos</li>
            <li className="list-group-item">Mais de 25 variáveis de hábitos de pagamento em Machine Learning</li>
            <li className="list-group-item">Dados dos sócios e quadro societário</li>
            <li className="list-group-item">Atrasos, restritivos, ações e demais apontamentos</li>
            <li className="list-group-item">Volume e valor das transações do cadastro positivo e de boletos bancários</li>
            <li className="list-group-item">Porte, CNAE, região e segmentação.</li>
          </ul>
          <p><b>*</b> Os dados serão sempre adequados ao perfil, porte, segmento do cliente de forma que todos ou uma seleção deles será utilizada sempre com o objetivo de apresentar os melhores resultados.</p>
          <br></br>
          <h4 className="m-0 font-weight-bold text-primary">Aplicações</h4>
          <p>O Quod Score PJ com boletos pode ser aplicado em diversos momentos do ciclo de crédito do cliente</p>
          <ul className="list-group">
            <li className="list-group-item">Na aquisição de clientes, conhecendo e filtrando melhor o público potencial.</li>
            <li className="list-group-item">Avaliando a concessão e definição de limites de crédito para operações financeiros e vendas mercantis</li>
            <li className="list-group-item">Validando a capacidade financeira e operacional de toda a cadeia de fornecimento</li>
            <li className="list-group-item">Auxiliando na orientação foco na cobrança</li>
            <li className="list-group-item">Também como o maior insumo na construção de políticas de crédito, dentro de motores e decisões automatizadas.</li>
          </ul>
          <p>O Quod Score PJ com boletos diferencia-se de todas as outras soluções existentes no mercado por utilizar a melhor capacidade de modelagem analítica do mercado, bem como a utilização da maior quantidade de dados já vista na construção de um score de crédito.
            Englobando a combinação de uma matriz de 12 diferentes fórmulas estatísticas. Sendo a única solução de mercado com extrema, robustez, eficiência e poder preditivo, independentemente do porte da empresa analisada, conforme ilustrado na figura abaixo</p>
          <img src={segmento} width={650} />
          <p>Além da pontuação, o modelo também retorna os Reason Codes, que são frases que explicam os motivos pelos quais o CNPJ consultado não atingiu a pontuação máxima do SCORE. </p>
        </>
      ) : (
        <div className="modal-body">
          <h4 className="m-0 font-weight-bold text-success">Como ler o Quod Score?</h4>
          <br></br>
          <p>O Quod score é uma pontuação baseada principalmente nos dados do <b>cadastro positivo</b>, calculada a partir de técnicas estatísticas avançadas, que vai de 300 a 1000. Com ele você tem informações mais precisas sobre a probabilidade de pagamento do consumidor para tomar suas decisões com segurança.</p>
          <p>Quanto mais alto o score,<b> maior a chance de o consumidor pagar</b> seus compromissos financeiros nos próximos meses.</p>
          <p>Para facilitar sua análise, nós dividimos a pontuação de acordo com a probabilidade média de um grupo de consumidores honrar seus pagamentos em risco alto, médio e baixo.</p>
          <h4 className="m-0 font-weight-bold text-primary">Entenda o Cadastro Positivo</h4>
          <br></br>
          <p>O banco de dados do cadastro positivo <b>vai além de atrasos em pagamentos.</b> Analisamos mais de 100 atributos compartilhados por diversas fontes, considerando também os hábitos de pagamento do cliente e seu histórico de crédito.</p>
          <br></br>
          <h4 className="m-0 font-weight-bold text-info">Tipos de informações analisadas:</h4>
          <br></br>
          <ul className="list-group">
            <li className="list-group-item">Financiamentos</li>
            <li className="list-group-item">Empréstimos</li>
            <li className="list-group-item">Consórcio</li>
            <li className="list-group-item">Cheque Especial</li>
            <li className="list-group-item">Cartão de Crédito</li>
            <li className="list-group-item">Período de Contrato</li>
            <li className="list-group-item">Quantidade de contratos</li>
            <li className="list-group-item">Tipo de Produtos Contratados</li>
            <li className="list-group-item">Período Negativado</li>
            <li className="list-group-item">Quantidade de Negativações</li>
            <li className="list-group-item">Atrasos Vigentes</li>
            <li className="list-group-item">histórico de Atrasos</li>
          </ul>
          <br></br>
          <h4 className="m-0 font-weight-bold text-secondary">Origem dos dados positivos:</h4>
          <br></br>
          <ul className="list-group">
            <li className="list-group-item">Bancos</li>
            <li className="list-group-item">Instituições Financeiras</li>
            <li className="list-group-item">Empresas de Telefonia</li>
            <li className="list-group-item">Empresas de Energia Elétrica</li>
            <li className="list-group-item">Empresas de Água</li>
            <li className="list-group-item">Empresas de Gás</li>
            <li className="list-group-item">Empresas de Varejo</li>
            <li className="list-group-item">Empresas de Atacado</li>
            <li className="list-group-item">Fintechs</li>
            <li className="list-group-item">Empresas de Cartões</li>
            <li className="list-group-item">Empresas de Empréstimos</li>
          </ul>
        </div>
      )}
    </>
  );

  // ─────────────────────────────────────────────────────────────────
  // NEW LAYOUT (isConsultaNova)
  // ─────────────────────────────────────────────────────────────────
  if (isConsultaNova) {
    return (
      <>
        <div
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)',
            padding: '24px',
          }}
        >
          {/* Card Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h3
                style={{
                  fontSize: '1rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: '#4B5563',
                  margin: 0,
                }}
              >
                {label}
              </h3>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#E5E7EB', minWidth: '40px' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Toggle button */}
              <button
                onClick={() => setUseHighcharts(v => !v)}
                title={useHighcharts ? 'Trocar para SVG nativo' : 'Trocar para Highcharts'}
                style={{
                  background: 'none',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '5px 10px',
                  cursor: 'pointer',
                  color: '#6B7280',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <i className={useHighcharts ? 'pi pi-chart-pie' : 'pi pi-chart-bar'} style={{ fontSize: '12px' }} />
                {useHighcharts ? 'Highcharts' : 'SVG Nativo'}
              </button>
              <span
                style={{
                  backgroundColor: riscoConfig.bg,
                  color: riscoConfig.text,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  border: `1px solid ${riscoConfig.border}`,
                }}
              >
                Risco {riscoConfig.label}
              </span>
            </div>
          </div>

          {/* Gauge + Info side-by-side */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'center', marginBottom: '24px' }}>
            {/* GAUGE */}
            <div style={{ flex: '0 0 auto', display: 'flex', justifyContent: 'center' }}>
              {useHighcharts ? (
                <div style={{ width: '220px' }}>
                  <ScoreGaugeHighcharts score={score} labelColor={riscoConfig.color} />
                </div>
              ) : (
                <ScoreGaugeSVG score={score.score} color={riscoConfig.color} />
              )}
            </div>

            {/* Right info */}
            <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Mensagem restritiva callout */}
              <div
                style={{
                  backgroundColor: riscoConfig.bg,
                  border: `1px solid ${riscoConfig.border}`,
                  borderRadius: '12px',
                  padding: '14px 16px',
                }}
              >
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <i className="pi pi-exclamation-triangle" style={{ color: riscoConfig.color, marginTop: '2px' }}></i>
                  <div>
                    <span style={{ fontWeight: 700, color: riscoConfig.color, fontSize: '0.85rem' }}>
                      Nível de Risco: {riscoConfig.label}
                    </span>
                    {score.mensagemInformativa && (
                      <p style={{ margin: '4px 0 0 0', fontSize: 'small', color: riscoConfig.text, lineHeight: 1.5 }}>
                        {score.mensagemInformativa}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Segmento / Porte row */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {score.segmento && (
                  <div style={{ flex: '1 1 120px' }}>
                    <span style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: '4px' }}>
                      Segmento
                    </span>
                    <span style={{ fontSize: 'small', fontWeight: 600, color: '#374151' }}>{score.segmento}</span>
                  </div>
                )}
                {score.porte && (
                  <div style={{ flex: '1 1 120px' }}>
                    <span style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: '4px' }}>
                      Porte
                    </span>
                    <span style={{ fontSize: 'small', fontWeight: 600, color: '#374151' }}>{score.porte}</span>
                  </div>
                )}
              </div>

              <Button
                label="Entenda o Score"
                icon="pi pi-question-circle"
                className="p-button-outlined p-button-sm"
                style={{
                  borderRadius: '8px',
                  fontWeight: 600,
                  color: '#183462',
                  borderColor: '#183462',
                  alignSelf: 'flex-start',
                  padding: '6px 14px',
                }}
                onClick={() => setVisible(true)}
              />
            </div>
          </div>

          {/* Risk bar */}
          <RiskBar scoreValue={score.score} />

          {/* Motivos */}
          {score.motivos && score.motivos.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <h4 style={{ fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#4B5563', margin: 0 }}>
                  Motivos
                </h4>
                <div style={{ flex: 1, height: '1px', backgroundColor: '#E5E7EB' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {score.motivos.map((motivo: string, idx: number) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      gap: '10px',
                      alignItems: 'flex-start',
                      padding: '10px 14px',
                      backgroundColor: '#FFF7ED',
                      borderRadius: '8px',
                      border: '1px solid #FED7AA',
                    }}
                  >
                    <i className="pi pi-info-circle" style={{ color: '#F59E0B', marginTop: '2px', flexShrink: 0 }}></i>
                    <span style={{ fontSize: 'small', color: '#92400E', lineHeight: 1.5 }}>{motivo}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Perfil de Mercado / Pontualidade */}
        {score.perfilMercado && (
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)',
              padding: '24px',
              marginTop: '16px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#4B5563', margin: 0 }}>
                {t('indicadores')}
              </h3>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#E5E7EB' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
              <div
                style={{
                  padding: '16px',
                  borderRadius: '12px',
                  backgroundColor: '#F0F9FF',
                  border: '1px solid #BAE6FD',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <i className="pi pi-shopping-cart" style={{ color: 'rgb(25, 118, 210)' }}></i>
                  <span style={{ fontWeight: 700, fontSize: 'small', color: '#1E3A8A' }}>Perfil de Mercado</span>
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'rgb(25, 118, 210)', marginBottom: '4px' }}>
                  {score.perfilMercado}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>{score.mensagemPerfilMercado}</div>
              </div>
              {score.pontualidadePagamento && (
                <div
                  style={{
                    padding: '16px',
                    borderRadius: '12px',
                    backgroundColor: '#F0FDF4',
                    border: '1px solid #BBF7D0',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <i className="pi pi-dollar" style={{ color: 'rgb(0, 121, 107)' }}></i>
                    <span style={{ fontWeight: 700, fontSize: 'small', color: '#065F46' }}>Pontualidade de Pagamento</span>
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'rgb(0, 121, 107)', marginBottom: '4px' }}>
                    {score.pontualidadePagamento}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>{score.mensagemPontualidadePagamento}</div>
                </div>
              )}
            </div>
          </div>
        )}

        <Dialog header="Entenda o Score" visible={visible} style={{ width: '50vw' }} onHide={() => setVisible(false)} footer={footerContent}>
          <EntendaScoreContent />
        </Dialog>
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────────
  // LEGACY LAYOUT
  // ─────────────────────────────────────────────────────────────────
  const headerMercado = () => (
    <div className="flex-score align-items-center" style={{ padding: '1.5rem' }}>
      <div className="flex-score justify-content-center align-items-center p-2 border-round" style={{ backgroundColor: 'rgb(100, 181, 246)' }}>
        <i className="pi pi-shopping-cart" style={{ color: "rgb(25, 118, 210)" }}></i>
      </div>
      <span className="text-xl ml-2 font-semibold">Perfil de Mercado</span>
    </div>
  );

  const headerPontualidade = () => (
    <div className="flex-score align-items-center" style={{ padding: '1.5rem' }}>
      <div className="flex-score justify-content-center align-items-center p-2 border-round" style={{ backgroundColor: 'rgb(77, 182, 172)' }}>
        <i className="pi pi-dollar" style={{ color: "rgb(0, 121, 107)" }}></i>
      </div>
      <span className="text-xl ml-2 font-semibold">Pontualidade de Pagamento</span>
    </div>
  );

  const options = {
    chart: {},
    title: {
      text: score.score + '',
      align: 'center',
      verticalAlign: 'middle',
      y: 90,
      style: { fontSize: '40px', color: labelColor },
    },
    credits: { enabled: false },
    tooltip: { enabled: false },
    plotOptions: {
      pie: {
        allowPointSelect: false,
        dataLabels: { enabled: false, distance: -50, style: { fontWeight: 'bold', color: 'white' } },
        startAngle: -90,
        endAngle: 90,
        center: ['50%', '75%'],
      },
    },
    series: [{
      type: 'pie',
      name: '',
      innerSize: '60%',
      data: [
        { name: '', y: score.score, color: labelColor, selected: true, showInLegend: false },
        { name: '', y: 1000 - score.score, color: '#ccc', selected: true, showInLegend: false },
      ],
    }],
  };

  return (
    <>
      <div className="pagebreak"></div>
      <Panel header={label}>
        <div className="grid">
          <div className="col p-fluid flex flex-wrap">
            <div>
              <HighchartsReact highcharts={Highcharts} options={options} />
            </div>
          </div>
          <div className="col">
            <div style={{ textAlign: 'center', paddingTop: '5rem' }}>
              {score.segmento && (
                <>
                  <span style={{ color: '#5a5c69', fontWeight: '700', fontSize: '1.25rem' }}>{t('motivoScore')}</span>
                  <br></br>
                  <span style={{ color: '#5a5c69', fontWeight: '500', fontSize: '1.0rem' }}>{score.segmento}</span>
                  <br></br>
                  <br></br>
                </>
              )}
              {score.porte && (
                <>
                  <br></br>
                  <span style={{ color: '#5a5c69', fontWeight: '700', fontSize: '1.25rem' }}>{t('porte')}</span>
                  <br></br>
                  <span style={{ color: labelColor, fontWeight: '700', fontSize: '1.5rem' }}>{score.porte}</span>
                </>
              )}
              <br></br>
              <br></br>
              <Button className="button_score" label="Entenda o Score" icon="pi pi-question-circle" style={{ backgroundColor: '#183462', border: 'none' }} onClick={() => setVisible(true)} />
              <Dialog header="Entenda o Score" visible={visible} style={{ width: '50vw' }} onHide={() => setVisible(false)} footer={footerContent}>
                <EntendaScoreContent />
              </Dialog>
            </div>
          </div>
        </div>
        <div className="grid">
          <div className="col border-right-1 surface-border">
            <ScoreBar></ScoreBar>
          </div>
          <div className="col">
            <section style={{ marginTop: "2rem" }}>
              <h3 style={{ fontSize: "1.25rem" }}>Motivos</h3>
              {score.motivos && (
                <div>
                  <ul style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {score.motivos.map((motivo: any, index: number) => (
                      <li key={index}><span>{motivo}</span></li>
                    ))}
                  </ul>
                </div>
              )}
              {!score.motivos && <span>Não Informado</span>}
            </section>
          </div>
        </div>
      </Panel>
      <br></br>
      {score.perfilMercado && (
        <Panel header={t('indicadores')}>
          <div className="grid">
            <div className="col p-fluid flex flex-wrap">
              <Card className="flex-score flex-column" header={headerMercado} style={{ color: 'rgb(100, 181, 246)', borderLeft: '4px solid', background: 'rgb(248,248,255)', height: '433px' }}>
                <div className="grid mt-3">
                  <div className="col-6 flex-score flex-column p-3 text-center border-right-1 surface-border">
                    <span className="text-color text-2xl font-semibold">{score.perfilMercado}</span>
                    <span className="text-color font-semibold">{score.mensagemPerfilMercado}</span>
                  </div>
                  <div className="col-6 flex-score flex-column p-3 text-center">
                    <span className="text-color text-2xl font-semibold">Conceito</span>
                    <span className="text-color font-semibold">Apresenta uma visão de como o consumidor se relaciona com o mercado, com base em produtos financeiros que utiliza (cartão de crédito, cheque especial, empréstimos entre outros), quantidade e a frequência de contratações, quanto maior a nota mais saudavel o relacionamento com o mercado.</span>
                  </div>
                </div>
              </Card>
            </div>
            <div className="col p-fluid flex flex-wrap">
              <Card className="flex-score flex-column" header={headerPontualidade} style={{ color: 'rgb(77, 182, 172)', borderLeft: '4px solid', background: 'rgb(248,248,255)', height: '433px' }}>
                <div className="grid mt-3">
                  <div className="col-6 flex-score flex-column p-3 text-center border-right-1 surface-border">
                    <span className="text-color text-2xl font-semibold">{score.pontualidadePagamento}</span>
                    <span className="text-color font-semibold">{score.mensagemPontualidadePagamento}</span>
                  </div>
                  <div className="col-6 flex-score flex-column p-3 text-center">
                    <span className="text-color text-2xl font-semibold">Conceito</span>
                    <span className="text-color font-semibold">Indica a pontualidade de pagamento do consumidor, como arca com seus compromissos financeiros, se possui atrasos vigentes, se efetua seus pagamentos em dia.</span>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </Panel>
      )}
    </>
  );
}
